import {
  Button,
  Flex,
  HStack,
  Spinner,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { RiAddLine } from "react-icons/ri";
import ActiveStudentPhrase from "./audio-components/ActiveStudentPhrase";
import RecordingSettings from "./audio-components/RecordingSettings";
import DataTable from "./data-table/DataTable";
import {
  AudioFile,
  createDefaultPhrases,
  createStudents,
  db,
  DEFAULT_STUDENT_NUM,
  getActiveAudioFile,
  Phrase,
  Student,
} from "./db";
import AddPhraseDialog from "./dialogs/AddPhraseDialog";
import RestoreSessionDialog from "./dialogs/RestoreSessionDialog";
import Header from "./header/Header";
import { defaultPhrases } from "./initial-data";

export interface RecordingOverrideControls {
  forceStop(): void;
}

export interface RecordingTools {
  mediaRecorder: MediaRecorder;
  analyser: AnalyserNode;
}

export type DataCollectionState = "complete" | "incomplete";
export type RecordingMode = "default" | "auto";
export type RecordingOrder = "default" | "random";
export type RecordingDevice = "microphone" | "micro:bit";
export type LayoutOption = "horizontal" | "vertical";
export const DEFAULT_SAMPLE_LENGTH = 1000;
export const DEFAULT_COUNTDOWN = 3;

export interface ActiveCell {
  student?: Student;
  phrase?: Phrase;
}

export interface Settings {
  recordingMode: RecordingMode;
  recordingOrder: RecordingOrder;
  countdown: number;
  sampleLength: number;
  schoolYear?: number;
  location?: string;
  recordingDevice?: RecordingDevice;
  layout: LayoutOption;
}

const defaultSettings: Settings = {
  recordingMode: "default",
  recordingOrder: "default",
  countdown: DEFAULT_COUNTDOWN,
  sampleLength: DEFAULT_SAMPLE_LENGTH,
  layout: "vertical",
};

const Workbench = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [phrases, setPhrases] = useState<Phrase[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [activeCell, setActiveCell] = useState<ActiveCell>({
    student: undefined,
    phrase: undefined,
  });

  const getSettings = useCallback((): Settings => {
    const localSettings = localStorage.getItem("settings");
    if (localSettings) {
      return JSON.parse(localSettings);
    } else {
      localStorage.setItem("settings", JSON.stringify(defaultSettings));
      return defaultSettings;
    }
  }, []);

  const resetSettings = useCallback(() => {
    localStorage.setItem("settings", JSON.stringify(defaultSettings));
    setSettings(defaultSettings);
  }, []);

  const [settings, setSettings] = useState<Settings>(getSettings());

  const setSettingsSafe = (settings: Settings) => {
    recordingOverrideCallbackRef.current?.forceStop();
    setSettings(settings);
  };

  useEffect(() => {
    if (
      students.every((student) => student.phrases.length === phrases.length)
    ) {
      setDataCollectionState("complete");
    } else {
      setDataCollectionState("incomplete");
    }
  }, [students, phrases]);

  const updateStudents = useCallback(async (): Promise<Student[]> => {
    const students = await db.students.toArray();
    setStudents(students);
    return students;
  }, []);

  const updatePhrases = useCallback(async (): Promise<Phrase[]> => {
    const phrases = await db.phrases.toArray();
    setPhrases(phrases);
    return phrases;
  }, []);

  const handleChangeActiveRecording = useCallback(
    (student: Student, phrase: Phrase) => {
      setActiveCell({ student, phrase });
    },
    []
  );

  const settingsAreDefault = useCallback(() => {
    return JSON.stringify(settings) === JSON.stringify(defaultSettings);
  }, [settings]);

  const handleRestart = useCallback(async () => {
    try {
      setIsLoading(true);
      await db.audioFiles.clear();
      await db.phrases.clear();
      await db.students.clear();
      await createStudents();
      await createDefaultPhrases();
      const localStudents = await updateStudents();
      const localPhrases = await updatePhrases();
      resetSettings();
      handleChangeActiveRecording(localStudents[0], localPhrases[0]);
      setIsLoading(false);
    } catch (err) {
      console.error(`Failed to delete database: ${err}`);
    }
  }, [
    updateStudents,
    updatePhrases,
    handleChangeActiveRecording,
    resetSettings,
  ]);

  const initLoad = useRef<boolean>(true);
  const restoreSessionDisclosure = useDisclosure();
  useEffect(() => {
    const checkForExistingSession = async () => {
      const students = await updateStudents();
      const phrases = await updatePhrases();
      const audioFiles = await db.audioFiles.count();
      if (
        !students.length &&
        !phrases.length &&
        !audioFiles &&
        settingsAreDefault()
      ) {
        return handleRestart();
      }
      handleChangeActiveRecording(students[0], phrases[0]);
      const studentsEdited = students.some((s) => s.edited);
      const phrasesEdited =
        phrases.map((p) => p.text).join() !==
        defaultPhrases.map((p) => p.text).join();
      if (
        !settingsAreDefault() ||
        phrasesEdited ||
        studentsEdited ||
        students.length !== DEFAULT_STUDENT_NUM ||
        !!audioFiles
      ) {
        restoreSessionDisclosure.onOpen();
      }
      setIsLoading(false);
    };

    if (initLoad.current) {
      initLoad.current = false;
      checkForExistingSession();
    }
  }, [
    handleChangeActiveRecording,
    phrases,
    restoreSessionDisclosure,
    updateStudents,
    updatePhrases,
    settingsAreDefault,
    handleRestart,
  ]);

  const addPhraseToStudent = useCallback(
    async (studentId: number, phrase: string) => {
      try {
        const student = await db.students.get(studentId);
        db.students.update(studentId, {
          phrases: [...student!.phrases, phrase],
        });
        await updateStudents();
      } catch (err) {
        console.error(`Failed to update student: ${err}`);
      }
    },
    [updateStudents]
  );

  const getNextStudent = useCallback((): Student => {
    let nextStudent: Student;
    const studentIndex = students.findIndex(
      (s) => s.id === activeCell.student?.id
    );
    if (studentIndex === students.length - 1) {
      nextStudent = students[0];
    } else {
      nextStudent = students[studentIndex + 1];
    }
    return nextStudent;
  }, [activeCell.student?.id, students]);

  const getNextPhrase = useCallback(
    (nextStudent: Student): Phrase | undefined => {
      let nextPhrase = activeCell.phrase;
      const phraseIndex = phrases.findIndex(
        (p) => p.id === activeCell.phrase?.id
      );
      if (settings.recordingOrder === "default") {
        if (phraseIndex === phrases.length) {
          return;
        }
        if (nextStudent === students[0]) {
          nextPhrase = phrases[phraseIndex + 1];
        }
        return nextPhrase;
      } else {
        const unspokenPhrases = phrases.filter(
          (phrase) => !nextStudent.phrases.includes(phrase.text)
        );
        if (unspokenPhrases.length === 0) {
          return;
        }
        nextPhrase =
          unspokenPhrases[Math.floor(Math.random() * unspokenPhrases.length)];
        return nextPhrase;
      }
    },
    [activeCell.phrase, phrases, settings.recordingOrder, students]
  );

  const [dataCollectionState, setDataCollectionState] =
    useState<DataCollectionState>("incomplete");
  const [finished, setFinished] = useState<boolean>(false);

  const handleNextStudentPhrase = useCallback(async () => {
    const nextStudent = getNextStudent();
    const nextPhrase = getNextPhrase(nextStudent);
    if (nextPhrase) {
      setActiveCell({ student: nextStudent, phrase: nextPhrase });
    } else {
      setActiveCell({ student: undefined, phrase: phrases[0] });
      setFinished(true);
    }
  }, [getNextPhrase, getNextStudent, phrases]);

  const handleAudioRecorded = useCallback(
    async (audioFile: AudioFile) => {
      if (audioFile.id) {
        try {
          await db.audioFiles.update(audioFile.id, audioFile);
        } catch (err) {
          console.error(`Failed to update audio file: ${err}`);
        }
      } else {
        try {
          await db.audioFiles.add(audioFile);
          await addPhraseToStudent(audioFile.studentId, audioFile.phrase);
        } catch (err) {
          console.error(`Failed to add audio file: ${err}`);
        }
      }
    },
    [addPhraseToStudent]
  );

  const handleRestoreSession = useCallback(
    async (restoreSession: boolean) => {
      if (!restoreSession) {
        await handleRestart();
      }
      restoreSessionDisclosure.onClose();
    },
    [handleRestart, restoreSessionDisclosure]
  );

  const addStudent = useCallback(async () => {
    await createStudents(1);
    await updateStudents();
  }, [updateStudents]);

  const addPhraseDisclosure = useDisclosure();

  const recordingOverrideCallbackRef = useRef<RecordingOverrideControls | null>(
    null
  );

  return (
    <>
      <VStack height="100vh" width="100vw" maxW="100%">
        <Header
          onRestart={handleRestart}
          settings={settings}
          setSettings={setSettingsSafe}
          students={students}
          updateStudents={updateStudents}
        />
        {isLoading ? (
          <VStack
            height="100%"
            width="100%"
            justifyContent="center"
            spacing={3}
          >
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="xl"
            />
            <Text fontSize="3xl">Loading...</Text>
          </VStack>
        ) : (
          <Flex
            py={5}
            px={10}
            gap={10}
            width="100%"
            height={settings.layout === "vertical" ? "unset" : "100%"}
            overflow={settings.layout === "vertical" ? "unset" : "hidden"}
            alignItems="center"
            justifyContent={
              settings.layout === "vertical" ? "center" : "space-between"
            }
            flexDir={settings.layout === "vertical" ? "column-reverse" : "row"}
          >
            <VStack
              width="full"
              maxW={settings.layout === "vertical" ? "max(80%, 1000px)" : "60%"}
              height="100%"
              spacing={5}
            >
              <HStack alignSelf="flex-start">
                <Button
                  size="md"
                  onClick={addPhraseDisclosure.onOpen}
                  rightIcon={<RiAddLine />}
                >
                  Add phrase
                </Button>
                <Button
                  size="md"
                  onClick={addStudent}
                  rightIcon={<RiAddLine />}
                >
                  Add student
                </Button>
              </HStack>
              <DataTable
                display="block"
                overflowX="auto"
                students={students}
                phrases={phrases}
                activeStudentId={activeCell.student?.id}
                activePhraseId={activeCell.phrase?.id}
                onChangeActiveRecording={handleChangeActiveRecording}
                updateStudents={updateStudents}
                updatePhrases={updatePhrases}
                recordingOverrideCallbackRef={recordingOverrideCallbackRef}
                layout={settings.layout}
              />
            </VStack>

            <VStack
              w="full"
              maxW="max(40%, 500px)"
              alignSelf={settings.layout === "vertical" ? "center" : "stretch"}
              spacing={10}
              overflowY={settings.layout === "vertical" ? "unset" : "auto"}
            >
              {!activeCell.student || !activeCell.phrase ? (
                <Text fontSize="4xl" fontWeight="semibold">
                  Please {students.length ? "select" : "add"} a student to
                  record
                </Text>
              ) : (
                <>
                  <ActiveStudentPhrase
                    student={activeCell.student}
                    phrase={activeCell.phrase}
                    fetchAudio={getActiveAudioFile}
                    onAudioRecorded={handleAudioRecorded}
                    onNextStudentPhrase={handleNextStudentPhrase}
                    recordingMode={settings.recordingMode}
                    countdownSetting={settings.countdown}
                    recordingOverrideCallbackRef={recordingOverrideCallbackRef}
                    sampleLength={settings.sampleLength}
                    updateStudents={updateStudents}
                  />

                  <RecordingSettings
                    settings={settings}
                    setSettings={setSettingsSafe}
                  />
                </>
              )}
              {finished && dataCollectionState === "complete" && (
                <Text fontSize="lg">
                  Recordings have now been collected for each student. Please
                  download the data by clicking the "Download data" at the top
                  of the page.
                </Text>
              )}
              {finished && dataCollectionState === "incomplete" && (
                <Text fontSize="lg">
                  You are missing some recordings. Please check the table to
                  select students where data has not been recorded.
                </Text>
              )}
            </VStack>
          </Flex>
        )}
      </VStack>
      {restoreSessionDisclosure.isOpen && (
        <RestoreSessionDialog
          isOpen={restoreSessionDisclosure.isOpen}
          onHandleRestoreSession={handleRestoreSession}
        />
      )}
      {addPhraseDisclosure.isOpen && (
        <AddPhraseDialog
          isOpen={addPhraseDisclosure.isOpen}
          updatePhrases={updatePhrases}
          onClose={addPhraseDisclosure.onClose}
        />
      )}
    </>
  );
};

export default Workbench;
